<script setup>
import { ref, computed, watch, onMounted, onUnmounted, effect, nextTick } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import { useKeyboardStore } from '@/stores/keyboardStore';
import { useQzStore } from '@/stores/qzStore';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { jsPDF } from "jspdf";
import JsBarcode from 'jsbarcode';
import PayLaterToggle from '@/components/Left/Buttons/PayLaterToggle.vue';
import GiftReceiptToggle from '@/components/Left/Buttons/GiftReceiptToggle.vue';

const authStore = useAuthStore();
const orderStore = useOrderStore();
const keyboardStore = useKeyboardStore();
const qzStore = useQzStore();
const toast = useToast();
const doc = new jsPDF();

// doc.text("Hello world!", 10, 10);
// doc.save("a4.pdf");

const currentBranch = window.currentBranch;
const loading = ref(false);
const invoiceId = ref(null);
const barcodeSvgString = ref('');
const barcodeSvgStringForOrderId = ref('');
const barcodeSvgStringForRemainingStoreCredit = ref('');
const barcodeSvgForCouponCodeReprint = ref('');

const couponCode = ref(null);
const storeCreditCode = ref('');
const storeCreditValue = ref(null);
const isStoreCreditPaymentMethodSelected = ref(false);
const remainingStoreCredit = ref(0);

const isCheckoutSuccessful = ref(false);
const wasNewSaleCompleted = ref(false);
const showFrontendIssueModal = ref(false);

function handleGlobalError() {
  showFrontendIssueModal.value = true;
}

function handleRescanOrderItems() {
  window.localStorage.removeItem('cachedProducts');
  orderStore.clearOrder();
  authStore.isCheckoutPopupVisible = false;
  showFrontendIssueModal.value = false;
  window.location.reload();
}

const isInvoiceVisible = ref(false);
const selectedPaymentMethods = ref([]);
const payLater = ref(false);
const isGiftReceipt = ref(false);

function convertNewlines(text) {
  if (!text) return '';
  return text.replace(/\n/g, '<br>');
}

async function getInvoiceElement() {
  let invoiceEl = document.querySelector(".invoice-print");
  if (!invoiceEl) {
    isInvoiceVisible.value = true;
    await nextTick();
    invoiceEl = document.querySelector(".invoice-print");
    if (!invoiceEl) {
      await new Promise(r => setTimeout(r, 100)); // wait a bit more
      invoiceEl = document.querySelector(".invoice-print");
    }
  }
  return invoiceEl;
}

async function printHtmlContent(content) {
  const styles = Array.from(document.styleSheets)
    .map((styleSheet) => {
      try {
        return Array.from(styleSheet.cssRules).map((rule) => rule.cssText).join("\n");
      } catch (e) {
        return "";
      }
    })
    .join("\n");

  const printWindow = window.open("", "_blank");
  if (!printWindow) {
      // toast.error('Pop-up blocked. Please allow pop-ups for this site to print.');
      return;
  }
  printWindow.document.open();
  printWindow.document.write(`
      <html>
        <head>
          <title>Print</title>
          <style>
            ${styles}
            @media print {
              body, html {
                height: auto !important;
                overflow: visible !important;
              }
              .invoice-print {
                height: auto !important;
                max-height: none !important;
                overflow: visible !important;
                page-break-inside: auto !important;
              }
              thead {
                display: table-header-group !important;
              }

              tbody {
                display: table-row-group !important;
              }

              tfoot {
                display: table-row-group !important;
              }

              table {
                page-break-before: avoid;
                page-break-after: avoid;
                page-break-inside: avoid;              
                border-collapse: collapse; 
              }

              h1, h2,h3, pre,p,div {
                page-break-before: avoid;
                page-break-after: avoid;
                page-break-inside: avoid;
              }

              .keep-together {
                page-break-inside: avoid;
              }

              .page-break {
                page-break-before: always;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">${content}</div>
          <script>
            window.onload = function() {
              document.body.style.height = 'auto';
              document.body.style.overflow = 'visible';
              window.print();
              setTimeout(() => window.close(), 500);
            };
          <\/script>
        </body>
      </html>
    `);
  printWindow.document.close();
  return new Promise(resolve => setTimeout(resolve, 500));
}

async function printInvoice() {
  if (!orderStore.isThisPaidManualOrder) {
    // QZ Tray Logic (Keep sequential as it talks to hardware driver directly)
    if (qzStore.useQzTrayPrint) {
        if (isGiftReceipt.value) {
            const originalGiftReceiptValue = isGiftReceipt.value;
            // 1. Normal
            isGiftReceipt.value = false;
            await nextTick();
            await qzStore.printInvoice();
            
            // 2. Gift
            isGiftReceipt.value = true;
            await nextTick();
            await qzStore.printInvoice();
            
            isGiftReceipt.value = originalGiftReceiptValue;
        } else {
            await qzStore.printInvoice();
        }
        handleClosePopup();
        return;
    }

    // Browser Print Logic (Combine into one window to avoid popup blockers)
    try {
        let contentToPrint = "";
        
        if (isGiftReceipt.value) {
             const originalGiftReceiptValue = isGiftReceipt.value;
             
             // 1. Normal
             isGiftReceipt.value = false;
             await nextTick();
             let el = await getInvoiceElement();
             if(!el) { toast.error("Could not generate receipt"); return; }
             contentToPrint += el.outerHTML;

             // 2. Gift
             isGiftReceipt.value = true;
             await nextTick();
             el = await getInvoiceElement();
             if(!el) { toast.error("Could not generate gift receipt"); return; }
             
             // Add page break and second receipt
             contentToPrint += `<div class="page-break" style="page-break-before: always; height:1px; display:block;"></div>`;
             contentToPrint += el.outerHTML;

             isGiftReceipt.value = originalGiftReceiptValue;
        } else {
             const el = await getInvoiceElement();
             if(!el) { toast.error("Could not generate receipt"); return; }
             contentToPrint = el.outerHTML;
        }

        await printHtmlContent(contentToPrint);
        handleClosePopup();

    } catch(e) {
        console.error(e);
        toast.error("Printing failed");
    }
  } else {
    handleClosePopup();
  }
}


function showInvoice() {
  const remaining = parseFloat(remainingAmount.value);

  // Check if remaining amount is effectively zero, allowing for small floating-point inaccuracies
  if (Math.abs(remaining) < 0.01) {
    isInvoiceVisible.value = true;
    console.log("Invoice is visible:", isInvoiceVisible.value);
  } else {
    toast.error('Please allocate the full payment before generating an invoice.');
    console.log("Invoice is not visible. Remaining amount:", remainingAmount.value);
  }
}

function handleClosePopup() {
  if (loading.value) return;
  const shouldAutoLogout = wasNewSaleCompleted.value && authStore.autoLogoutAfterSale;
  authStore.toggleCheckoutPopup();
  selectedPaymentMethods.value = [];
  storeCreditValue.value = null;
  storeCreditCode.value = '';
  remainingStoreCredit.value = 0;
  payLater.value = false;
  isGiftReceipt.value = false;
  if (isCheckoutSuccessful.value) {
    orderStore.clearOrder();
    orderStore.clearReceiptForReprint();
    isCheckoutSuccessful.value = false;
    isInvoiceVisible.value = false;
    invoiceId.value = null;
    barcodeSvgString.value = '';
    barcodeSvgStringForOrderId.value = '';
    barcodeSvgStringForRemainingStoreCredit.value = '';
    barcodeSvgForCouponCodeReprint.value = '';
    couponCode.value = null;
    storeCreditCode.value = '';
    storeCreditValue.value = null;
    remainingStoreCredit.value = 0;
    isStoreCreditPaymentMethodSelected.value = false;
  }
  wasNewSaleCompleted.value = false;
  if (shouldAutoLogout) {
    authStore.logout('Cashier');
  } else if (authStore.isUserLoggedIn) {
    authStore.logout('Manager');
  }
}

// function closeInvoice() {
//   isInvoiceVisible.value = false;
// }

// Function to handle page reload
const handlePageReload = () => {
  if (isCheckoutSuccessful.value) {
    orderStore.clearOrder(); // Call the clearOrder function
  }
};

// Watch for changes in isCheckoutSuccessful
watch(isCheckoutSuccessful, (newValue) => {
  if (newValue) {
    // Attach the event listener when checkout is successful
    window.addEventListener('beforeunload', handlePageReload);
  } else {
    // Remove the event listener if checkout is not successful
    window.removeEventListener('beforeunload', handlePageReload);
  }
});

// Clean up the event listener when the component is unmounted
onUnmounted(() => {
  window.removeEventListener('beforeunload', handlePageReload);
  window.removeEventListener('error', handleGlobalError);
  window.removeEventListener('unhandledrejection', handleGlobalError);
});


// Available payment methods
const paymentMethods = [
  { id: '1', name: 'Cash', icon: '/cash.svg' },
  { id: '2', name: 'Debit Card', icon: '/debitCard.png' },
  { id: '3', name: 'Visa', icon: '/visa.svg' },
  { id: '4', name: 'Master Card', icon: '/mastercard.svg' },
  { id: '5', name: 'Amex', icon: '/american-express.svg' },
  { id: '6', name: 'Interac Etransfer', icon: '/Interac.svg' },
  { id: '7', name: 'Store Credit', icon: '/coupon.svg'}
];

// Total order amount
const totalAmount = computed(() => orderStore.getOrderTotal);
const grandTotal = computed(() => orderStore.getGrandTotalForOverallDiscount);

const effectiveTotal = computed(() => {
  const value = orderStore.isOverallDiscount.value ? orderStore.getGrandTotalForOverallDiscount : orderStore.getOrderTotal;
  return Math.round(value * 100) / 100;
});


// Remaining balance to be paid (will not go negative)
const remainingAmount = computed(() => {
  const allocatedAmount = selectedPaymentMethods.value.reduce(
    (acc, method) => acc + parseFloat(method.amount || 0),
    0
  );
  return Math.max(effectiveTotal.value - allocatedAmount, 0).toFixed(2);
});

// Check remaining amount is grater than 0
const isRemainingAmountPositive = computed(() => parseFloat(remainingAmount.value) > 0);


// Function to add a payment method
function addPaymentMethod(paymentMethod) {
  const existingMethod = selectedPaymentMethods.value.find(method => method.id === paymentMethod.id);

  if (!existingMethod) {
    if (['Visa', 'Master Card', 'Amex', 'Debit Card', 'Interac Etransfer'].includes(paymentMethod.name)) {
      selectedPaymentMethods.value.push({
        id: paymentMethod.id,
        name: paymentMethod.name,
        amount: remainingAmount.value
      });
     } else if (paymentMethod.name === 'Store Credit') {
       selectedPaymentMethods.value.push({
         id: paymentMethod.id,
         name: paymentMethod.name,
         amount: (storeCreditValue.value ?? '0.00')
       });
       // Show invoice preview even if allocation isn't complete yet for Store Credit
       isInvoiceVisible.value = true;
     }
       else if (paymentMethod.name === 'Cash') {
        selectedPaymentMethods.value.push({
          id: paymentMethod.id,
          name: paymentMethod.name,
          amount: '0.00'
        });
        isInvoiceVisible.value = true;
      }
      else {
      selectedPaymentMethods.value.push({
        id: paymentMethod.id,
        name: paymentMethod.name,
        amount: '0.00'
      });
    }
    if (!['Store Credit', 'Cash'].includes(paymentMethod.name)) {
      showInvoice();
    }
  } else {
    toast.error(`${paymentMethod.name} is already selected.`);
  }
}


// handle clear the amount for a payment method
function clearPaymentAmount(method) {
  const methodToUpdate = selectedPaymentMethods.value.find(m => m.id === method.id);
  if (methodToUpdate) {
    methodToUpdate.amount = '0.00';
  }
}

const predictiveCashAmounts = computed(() => {
  const remaining = parseFloat(remainingAmount.value);

  if (isNaN(remaining) || remaining <= 0) {
    return [];
  }

  // Initialize suggestions set to avoid duplicates
  const suggestions = new Set();

  // Bill denominations for suggestions
  const billDenominations = [10, 20, 50, 100];

  // Start at the next multiple of 10 above the remaining amount
  let startAmount = Math.ceil(remaining / 10) * 10;
  suggestions.add(startAmount);

  // Generate suggestions based on bill denominations
  for (let increment of billDenominations) {
    let suggestedAmount = Math.ceil(remaining / increment) * increment;

    if (suggestedAmount > remaining) {
      suggestions.add(remaining);
      suggestions.add(suggestedAmount);
    }
  }

  // Return sorted suggestions as an array
  return Array.from(suggestions).sort((a, b) => a - b);
});

function setExactTotal(method) {
  const methodToUpdate = selectedPaymentMethods.value.find(m => m.id === method.id);
  if (methodToUpdate) {
    methodToUpdate.amount = totalAmount.value;
  }
}

function updatePaymentAmount(method, amount) {
  const methodToUpdate = selectedPaymentMethods.value.find(m => m.id === method.id);
  if (methodToUpdate) {
    methodToUpdate.amount = amount;
  }
}

// handle update cash amount based on button click
function setCashAmount(amount) {
  const cashMethod = selectedPaymentMethods.value.find(method => method.name === 'Cash');
  if (cashMethod) {
    cashMethod.amount = amount.toFixed(2); // Allow larger amounts to be set
  } else {
    addPaymentMethod({ id: '5', name: 'Cash', icon: '/cash.svg' });
    setCashAmount(amount);
  }
}

// Prevent negative input on typing
function preventNegativeInput(event) {
  const invalidChars = ['-', 'e', '+'];
  if (invalidChars.includes(event.key)) {
    event.preventDefault();
  }
}

const storeId = localStorage.getItem('branchId');


watch(selectedPaymentMethods, (newVal) => {
  const totalAllocated = newVal.reduce((acc, m) => acc + parseFloat(m.amount || 0), 0);
  const totalOrder = parseFloat(totalAmount.value);

  // Ensure total allocation matches the total order amount
  if (totalAllocated > totalOrder) {
    const excessAmount = totalAllocated - totalOrder;
    //adjustPreviousPaymentMethod(excessAmount, newVal[newVal.length - 1].id);
  }
}, { deep: true });

// Watcher to automatically adjust remaining amount for the last payment method
watch(selectedPaymentMethods, (newVal) => {
  if (newVal.length > 0 && remainingAmount.value <= 0) {
    newVal[newVal.length - 1].amount = (parseFloat(newVal[newVal.length - 1].amount) + parseFloat(remainingAmount.value)).toFixed(2);
  }
});

// handle remove payment method
function removePaymentMethod(methodId) {
  const index = selectedPaymentMethods.value.findIndex(method => method.id === methodId);
  if (index !== -1) {
    selectedPaymentMethods.value.splice(index, 1);
    toast.success('Payment method removed.');
  }
  if (methodId === '7') {
    storeCreditValue.value = null;
    storeCreditCode.value = '';
  }
}

// handle checkout and API submission
async function handleCheckout() {
  const token = localStorage.getItem('token') || localStorage.getItem('managerToken');
  if (orderStore.hasZeroPriceItem) {
    toast.error('Item with 0.00 price can not be checked out.');
    return
  }
  const hasInvalidDiscountZeroPrice = orderStore.state.orderItems.some(item => {
    const discount = Number(item?.Discount ?? 0);
    const price = Number(item?.Price ?? 0);
    return Number.isFinite(discount) && discount !== 100 && price === 0;
  });
  if (hasInvalidDiscountZeroPrice) {
    showFrontendIssueModal.value = true;
    return;
  }

  if (loading.value) return;
  loading.value = true;

  const remaining = parseFloat(remainingAmount.value);
  if (!orderStore.isThisPaidManualOrder) {
    if (selectedPaymentMethods.value.length === 0) {
      toast.error('Please select a payment method.');
      loading.value = false;
      return;
    }
  }

  const totalAllocated = selectedPaymentMethods.value.reduce(
    (acc, method) => acc + parseFloat(method.amount || 0),
    0
  );

  if(!orderStore.isThisPaidManualOrder) {
      if (Math.abs(remaining > 0.01)) {
      toast.error('Payment allocation does not match the total amount.');
      loading.value = false; // Reset loading state
      return;
    }
  }

  const payload = {
    
    InvoiceId: orderStore.state.InvoiceId || '',

    // Map order items to API format
    ItemList: orderStore.state.orderItems.map(item => ({
      ItemId: item.ItemId,
      ItemName: item.ItemName,
      Qty: item.Qty,
      Discount: orderStore.state.overallDiscount > 0 ? orderStore.state.overallDiscount : parseFloat(item.Discount),
      TaxesWaived: item.TaxesWaived,
      Rate: item.isIncludeTaxChecked ? item.Price : item.OriginalPrice,
      SalesPersonId: item.SalesPersonId || 'defaultSalesPersonId',
      StoredBarcodeCode: item.storedBarcodeCode || null,
    })),

    // Approval list (updated structure)
    ApprovalList: orderStore.state.approvalList.map(approval => ({
      ManagerId: approval.ManagerId || 'defaultManagerId',
      ManagerApprovalId: approval.ManagerApprovalId || 'defaultApprovalId',
      ItemId: approval.ItemId || null,
      isOverallDiscount: approval.isOverallDiscount || false,
      id: approval.id || '' // Using a function to generate unique IDs
    })),

    // Overall discount
    OverallDiscount: 0,

    // Customer details
    Customer: {
      id: orderStore.state.customer.id || '',
      Name: orderStore.state.customer.name || '',
      Phone: orderStore.state.customer.phone || '',
      Email: orderStore.state.customer.email || '',
      Note: orderStore.state.customer.note || ''
    },

    // Comments list
    Comments: orderStore.state.comments.map(comment => ({
      UserId: comment.userId || authStore.managerUser.id,
      Text: comment.text || ''
    })),

    // Payment details
    Payment: selectedPaymentMethods.value.map(method => ({
      PaymentMethodId: method.id,
      Amount: (method.id === '7' && checkStoreCreditValue.value) ? effectiveTotal.value : method.amount,
      Percentage: effectiveTotal.value ? ((parseFloat(method.amount) / effectiveTotal.value) * 100).toFixed(2) : '0.00',
      Change: method.name === 'Cash' ? changeAmount.value : '0.00',
      CouponCode: (method.id === '7') ? storeCreditCode.value : '',
    })),

    Taxes: orderStore.state.taxes.map(tax => ({
      TaxId: tax.type,
      Amount: tax.rate,
    })),

    isManualOrder: orderStore.isThisPaidManualOrder ? true : orderStore.state.isManualOrder,

    // Total order amount
    Total: effectiveTotal.value
  };

  try {
    if (!token) {
      toast.error('Authorization token not found. Please log in again.');
      loading.value = false;
      return;
    }

    const response = await axios.post('/api/Order/NewOrder', payload, {
      headers: {
        'store-id': storeId,
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.data.status === "200") {
      console.log("Are total equal?", response.data.total === effectiveTotal.value);
      console.log("Show totals", response.data.total, effectiveTotal.value);
      // if (response.data.total === effectiveTotal.value) {
      if (Math.abs(response.data.total - effectiveTotal.value) < .25) {

        toast.success('Order placed successfully');
        invoiceId.value = response.data.invoiceId;

        const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        JsBarcode(svgNode, invoiceId.value.replace('SAL-ORD-', ''), {
          format: 'CODE128',
          lineColor: '#000',
          width: 2,
          height: 80,
          displayValue: false
        });
        const svgMarkup = svgNode.outerHTML

        barcodeSvgString.value = svgMarkup
        orderStore.state.isManualOrder = false;
        // storeCreditValue.value = null;
        // storeCreditCode.value = '';

        // showInvoice();
        nextTick(() => {
          printInvoice();
          orderStore.setIsThisPaidManualOrder(false);
        });
        isCheckoutSuccessful.value = true;
        wasNewSaleCompleted.value = true;

        // Save to local storage
        const toPass = generateInvoice.value;
        toPass.totalAmount = toPass?.totalAmount.value;
        toPass.isGiftReceipt = isGiftReceipt.value;

        //  remainingStoreCredit.value = 0;

        if (!orderStore.isThisPaidManualOrder) {
          orderStore.saveReceiptToStorage(toPass);
        }

        //Deduct the qty locally
        orderStore.deductQuantities();

      } else {
        toast.error('Total in ERP and in the order don\'t match');
      }
    } else if (response.data.status === "500") {
      toast.error(`Error: ${response.data.message.join(', ')}`);
    } else {
      toast.error('Failed to place the order.');
    }
  } catch (error) {
    console.error('Checkout error:', error);
    toast.error('An error occurred during checkout.');
  } finally {
    loading.value = false;
  }
}

// Calculating the change when cash payment exceeds the total (specific to the cash method)
const changeAmount = computed(() => {
  const totalAllocated = selectedPaymentMethods.value.reduce(
    (acc, method) => acc + parseFloat(method.amount || 0), 0
  ); // Sum up amounts from all payment methods

  const totalOrder = parseFloat(totalAmount.value);

  if (totalAllocated > totalOrder) {
    const cashMethod = selectedPaymentMethods.value.find(method => method.name === 'Cash');
    if (cashMethod) {
      const cashPaid = parseFloat(cashMethod.amount || 0);
      const remainingToCover = totalOrder - (totalAllocated - cashPaid);

      if (cashPaid > remainingToCover) {
        return (cashPaid - remainingToCover).toFixed(2);
      }
    }
  }
  return '0.00';
});

const generateInvoice = computed(() => {
  if (orderStore.state.receiptForReprint) {
    invoiceId.value = orderStore.state.receiptForReprint.invoiceId;
    const customerName = orderStore.state.receiptForReprint.customerName;
    const Salesperson = orderStore.state.receiptForReprint.Salesperson;
    const issueReprint = authStore.currentUser || authStore.managerUser;
    const couponCodeForReprint = orderStore.state.receiptForReprint.couponCodeForReprint;
    const savedRestocking = orderStore.state.receiptForReprint.restockingFeeTotal;

        const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        JsBarcode(svgNode, invoiceId.value.replace('SAL-ORD-', ''), {
          format: 'CODE128',
          linepColor: '#000',
          width: 2,
          height: 80,
          displayValue: false
        });
        const svgMarkup = svgNode.outerHTML

        barcodeSvgString.value = svgMarkup
        console.log('Reprint receipt in chekout', orderStore.state.receiptForReprint);

        // For coupon code reprint
        const savedCouponCode = orderStore.state.receiptForReprint.couponCodeForReprint
        if (savedCouponCode  && savedCouponCode !== '') {
          const svgNodeForCouponCodeReprint = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          JsBarcode(svgNodeForCouponCodeReprint, couponCodeForReprint.replace('SAL-ORD-', ''), {
            format: 'CODE128',
            lineColor: '#000',
            width: 2,
            height: 80,
            displayValue: false
          });
          const svgMarkupForCouponCodeReprint = svgNodeForCouponCodeReprint.outerHTML;
          barcodeSvgForCouponCodeReprint.value = svgMarkupForCouponCodeReprint;
        }
        
    return {
      ...orderStore.state.receiptForReprint,
      customerName: customerName,
      Salesperson: Salesperson,
      invoiceId: invoiceId.value,
      issueReprint: issueReprint,
      isGiftReceipt: isGiftReceipt.value,
    };
  } 
  
  if (orderStore.state.returnPrint) {
        invoiceId.value = orderStore.state.returnPrint.orderId;
        const customerName = orderStore.state.returnPrint.customer;
        const restockingFeeTotal = orderStore.state.returnPrint.totalRestockingFee || 0;
        // OrderId
        const svgForOrderId = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        JsBarcode(svgForOrderId, orderStore.state.returnPrint.orderId, {
          format: 'CODE128',
          lineColor: '#000',
          width: 2,
          height: 80,
          displayValue: false
        });
        const svgMarkupForOrderId = svgForOrderId.outerHTML

        barcodeSvgStringForOrderId.value = svgMarkupForOrderId


        //coupon code
        if(authStore.selectedRefundMethod === 'StoreCredit') {
          couponCode.value = orderStore.state.couponCode;
          const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          JsBarcode(svgNode, couponCode.value.replace('SAL-ORD-', ''), {
            format: 'CODE128',
            lineColor: '#000',
            width: 2,
            height: 80,
            displayValue: false
          });
          const svgMarkup = svgNode.outerHTML

          barcodeSvgString.value = svgMarkup
        }
    return {
      ...orderStore.state.returnPrint,
      invoiceId: invoiceId.value,
      customerName: customerName,
      restockingFeeTotal: restockingFeeTotal,
    };
  }

  //how left value of store credit on new purchase reciept
  if ((storeCreditValue.value > effectiveTotal.value)) {
    remainingStoreCredit.value = storeCreditValue.value - effectiveTotal.value;
          // couponCode.value = storeCreditCode.value;
          // const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          // JsBarcode(svgNode, couponCode.value.replace('SAL-ORD-', ''), {
          //   format: 'CODE128',
          //   lineColor: '#000',
          //   width: 2,
          //   height: 80,
          //   displayValue: false
          // });
          // const svgMarkup = svgNode.outerHTML

          // barcodeSvgStringForRemainingStoreCredit.value = svgMarkup
  }

  // Otherwise, use the current order data (original implementation)
  const invoiceItems = orderStore.state.orderItems.map(item => ({
    name: item.ItemName || 'Unknown Item',
    quantity: item.Qty || 1,
    originalQty: 0,
    price: parseFloat(item.Price),
    total: parseFloat(item.Qty * item.Price),
    originalPrice: parseFloat( item.OriginalPrice),
    discount: item.Discount,
    sku: item.Sku,
  }));



  // Calculate subtotal
  const subtotal = parseFloat( invoiceItems.reduce((acc, item) => acc + parseFloat(item.total), 0).toFixed(2));

  // Calculate overall discount on the subtotal
  const overallDiscount = orderStore.state.overallDiscount || 0;
  const discountAmount = (subtotal * (overallDiscount / 100)).toFixed(2);

  // Calculate the taxable amount (subtotal minus the discount)
  const taxableAmount = (subtotal - discountAmount).toFixed(2);

  // Get taxes from store and calculate amounts
  const taxableItems = orderStore.state.orderItems.filter(item => !item.TaxesWaived);
  const taxableSubtotal = taxableItems.reduce((acc, item) => acc + (item.Qty * item.Price), 0);

  const taxes = orderStore.state.taxes.map(tax => ({
    type: tax.type,
    rate: tax.rate,
    amount: (taxableSubtotal * (tax.rate / 100)).toFixed(2)
  }));

  const customerName = orderStore.state.customer.name;
  const Salesperson = orderStore.state.orderItems[0]?.SalesPersonId?.fullName || orderStore.state.orderItems[0]?.SalesPersonId;

  // Calculate GST and PST (if they exist)
  const gst = taxes[0]?.amount || '0.00';
  const pst = taxes[1]?.amount || '0.00';

  // Calculate the final total amount
  const totalWithTaxes = (parseFloat(taxableAmount) +
    taxes.reduce((sum, tax) => sum + parseFloat(tax.amount), 0)).toFixed(2);

  const previewImage = orderStore.state.orderItems[0].ItemImage;
  const date = Date.now();

  const changeAmountForPrint = changeAmount.value;

  const isThisReturnReciept = false;
  const isThisCashReturn = false;
  
  const ramainingStoreCreditForReprnt = 0;

  return {
    items: invoiceItems,
    previewImage  : previewImage,
    selectedPaymentMethods : selectedPaymentMethods.value,
    invoiceId: invoiceId.value,
    subtotal: subtotal,
    discountAmount: discountAmount,
    taxes: taxes,
    gst: gst,
    pst: pst,
    totalAmount: effectiveTotal,
    date: date,
    customerName: customerName,
    Salesperson: Salesperson,
    changeAmount: changeAmountForPrint,
    remainingStoreCredit: remainingStoreCredit?.value.toFixed(2),
    isThisReturnReciept: isThisReturnReciept,
    couponCodeForReprint: '',
    isThisCashReturn: isThisCashReturn,
  };
});

const printStyles = `
  .sidebar-menu,
  .checkout-Left {
    display: none !important;
    background: transparent !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }

  .flex.bg-white.rounded-2xl.shadow-lg.p-6.w-full {
    box-shadow: none !important;
    border-radius: 0 !important;
  }

  .class-hide-on-print {
    display: none !important;
  }

  .class-hide-on-print body * {
    visibility: hidden !important;
  }

  ::-webkit-scrollbar-button {
    display: none !important;
    background: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  ::-webkit-scrollbar {
    width: 8px !important;
    background-color: #f0f0f0 !important;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #b0b0b0 !important;
    border-radius: 4px !important;
  }

  ::-webkit-scrollbar-button {
    display: none !important;
  }

  .invoice-print {
    visibility: visible !important;
    display: block !important;
    margin: 0 auto !important;
    width: 600px !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    position: absolute;
    max-height: 900px;
    overflow-y: scroll !important;
  }
  .invoice-print * {
    visibility: visible !important;
  }

  .invoice-div {
    background: transparent !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }

  .watermark {
    position: absolute;
    top: 40%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 4rem;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.1);
    border: 6px solid rgba(0, 0, 0, 0.1);
    padding: 1rem 3rem;
    z-index: 9999;
    pointer-events: none;
    white-space: nowrap;
    display: block !important;
    visibility: visible !important;
  }
`;
async function downloadPdf() {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4'
  });

  const invoiceElement = document.querySelector('.invoice-print');
  if (!invoiceElement) {
    console.error('No .invoice-print found');
    return;
  }

  // 1) Create a <style> tag for your print styles
  const styleTag = document.createElement('style');
  styleTag.innerHTML = printStyles;

  // 2) Append it so the page uses those rules
  document.head.appendChild(styleTag);

  // 3) Adjust the doc.html options if you want a certain width
  const pageWidth = doc.internal.pageSize.getWidth() - 40;

  await doc.html(invoiceElement, {
    callback: (doc) => {
      doc.save(`invoice-${invoiceId.value}.pdf`);

      // 4) Remove the style tag so your UI reverts
      document.head.removeChild(styleTag);
    },
    x: 20,
    y: 20,
    width: pageWidth,
    html2canvas: {
      scale: 1,
      windowWidth: pageWidth
    }
  });
}

watch(() => orderStore.state.receiptForReprint, (newReceipt) => {
  if (newReceipt) {
    // Set the invoice data from the saved receipt
    invoiceId.value = newReceipt.id;
    barcodeSvgString.value = newReceipt.barcodeSvg;
    
    // Show the invoice
    isCheckoutSuccessful.value = true;
    isInvoiceVisible.value = true;
    
    // Initialize gift receipt toggle from saved state
    if (newReceipt.isGiftReceipt) {
        isGiftReceipt.value = newReceipt.isGiftReceipt;
    } else {
        isGiftReceipt.value = false;
    }
  }
}, { immediate: true });

watch(() => orderStore.state.returnPrint, (newReceipt) => {
  if (newReceipt) {
    // Set the invoice data from the saved receipt
    invoiceId.value = newReceipt.id;
    barcodeSvgString.value = newReceipt.barcodeSvg;
    
    // Show the invoice
    isCheckoutSuccessful.value = true;
    isInvoiceVisible.value = true;
  }
}, { immediate: true });

//Keyboard shortcuts statt
onMounted(() => {
  window.addEventListener('error', handleGlobalError);
  window.addEventListener('unhandledrejection', handleGlobalError);
  keyboardStore.register('Alt+O', () => {
    if(orderStore.state.orderItems.length > 0) {
      if (!authStore.isCheckoutPopupVisible) {
        authStore.isCheckoutPopupVisible = true;
      }
    } else {
      toast.error('Please add order items before checking out.');
      return;
    }
  });

  keyboardStore.register('Alt+1', () => {
    if(orderStore.state.orderItems.length > 0) {
      if (!authStore.isCheckoutPopupVisible) {
        authStore.isCheckoutPopupVisible = true;
      }
    } else {
      toast.error('Please add order items before checking out.');
      return;
    }
    if (!selectedPaymentMethods.value.some(m => m.id === '1')) {
      addPaymentMethod(paymentMethods.find(m => m.id === '1'));
    }
    // toast.info('Selected Cash payment');
  });

  keyboardStore.register('Alt+2', () => {
    if(orderStore.state.orderItems.length > 0) {
      if (!authStore.isCheckoutPopupVisible) {
        authStore.isCheckoutPopupVisible = true;
      }
    } else {
      toast.error('Please add order items before checking out.');
      return;
    }
    if (!selectedPaymentMethods.value.some(m => m.id === '2')) {
      addPaymentMethod(paymentMethods.find(m => m.id === '2'));
    }
    // toast.info('Selected Debit Card payment');
  });

  keyboardStore.register('Alt+3', () => {
    if(orderStore.state.orderItems.length > 0) {
      if (!authStore.isCheckoutPopupVisible) {
        authStore.isCheckoutPopupVisible = true;
      }
    } else {
      toast.error('Please add order items before checking out.');
      return;
    }
    if (!selectedPaymentMethods.value.some(m => m.id === '3')) {
      addPaymentMethod(paymentMethods.find(m => m.id === '3'));
    }
    // toast.info('Selected Visa payment');
  });

  keyboardStore.register('Alt+4', () => {
    if(orderStore.state.orderItems.length > 0) {
      if (!authStore.isCheckoutPopupVisible) {
        authStore.isCheckoutPopupVisible = true;
      }
    } else {
      toast.error('Please add order items before checking out.');
      return;
    }
    if (!selectedPaymentMethods.value.some(m => m.id === '4')) {
      addPaymentMethod(paymentMethods.find(m => m.id === '4'));
    }
    // toast.info('Selected Master Card payment');
  });

  keyboardStore.register('Alt+5', () => {
    if(orderStore.state.orderItems.length > 0) {
      if (!authStore.isCheckoutPopupVisible) {
        authStore.isCheckoutPopupVisible = true;
      }
    } else {
      toast.error('Please add order items before checking out.');
      return;
    }
    if (!selectedPaymentMethods.value.some(m => m.id === '5')) {
      addPaymentMethod(paymentMethods.find(m => m.id === '5'));
    }
    // toast.info('Selected Amex payment');
  });

  keyboardStore.register('Alt+6', () => {
    if(orderStore.state.orderItems.length > 0) {
      if (!authStore.isCheckoutPopupVisible) {
        authStore.isCheckoutPopupVisible = true;
      }
    } else {
      toast.error('Please add order items before checking out.');
      return;
    }
    if (!selectedPaymentMethods.value.some(m => m.id === '6')) {
      addPaymentMethod(paymentMethods.find(m => m.id === '6'));
    }
    // toast.info('Selected Interac Etransfer payment');
  });

  keyboardStore.register('Alt+P', () => {
    handleCheckout();
  });

});

onUnmounted(() => {
  keyboardStore.unregister('Alt+O');
  keyboardStore.unregister('Alt+1');
  keyboardStore.unregister('Alt+2');
  keyboardStore.unregister('Alt+3');
  keyboardStore.unregister('Alt+4');
  keyboardStore.unregister('Alt+5');
  keyboardStore.unregister('Alt+6');
  keyboardStore.unregister('Alt+P');
});
//keyboard shortcuts end

// Handle load coupon code value
const isLoadingCreditValue = ref(false);
const getStoreCreditValue = async () => {
  
  try {
    if (!storeCreditCode.value) {
      toast.error('Please enter a coupon code.');
      return;
    }
    isLoadingCreditValue.value = true;

    const response = await axios.get(
      `/api/Order/VerifyCouponCode?couponCode=${storeCreditCode.value}`,
      {
        headers: {
          'Authorization': `Bearer ${authStore.managerToken ? authStore.managerToken : authStore.token}`,
          'store-id': authStore.branchId
        }
      }
    );

    if (response.data.status === 200) {
      console.log('Coupon code value:', response.data.total);
      storeCreditValue.value = response.data.total;
      isLoadingCreditValue.value = false;
      console.log('Store credit value:', storeCreditValue.value);
      const method = paymentMethods.find(m => m.name === 'Store Credit');
      updatePaymentAmount(method, storeCreditValue.value);
      
    } else {
      toast.error('Invalid coupon code. Please try again.');
      storeCreditValue.value = null;
      isLoadingCreditValue.value = false;
      storeCreditCode.value = '';
    }
  } catch (err) {
    console.error('Error fetching coupon code value:', err);
    toast.error('An error occurred while fetching the coupon code value.');
    isLoadingCreditValue.value = false;
    storeCreditValue.value = null;
    storeCreditCode.value = '';
  } finally {
    isLoadingCreditValue.value = false;
  }
};

const isStoreCreditPaymentMethodSelectedd = computed(() => {
  return selectedPaymentMethods.value.some(method => method.name === 'Store Credit');
});

// Force input filed reset to storeCreditValue
const amountShouldNotExceedstoreCreditValue = ( method, amount ) => {
  const methodToUpdate = selectedPaymentMethods.value.find(m => m.id === method.id);
  if (methodToUpdate) {
    if (parseFloat(amount) > parseFloat(storeCreditValue.value)) {
      // toast.error('Amount should not exceed store credit value.');
      methodToUpdate.amount = storeCreditValue.value;
    } else {
      methodToUpdate.amount = amount;
    }
  }
};

// Check if the store credit value is greater than
// the total amountr
const isStoreCreditValueGreaterThanTotal = computed(() => {
  if ((storeCreditValue.value && effectiveTotal.value) && (storeCreditValue.value > effectiveTotal.value)) {
    return effectiveTotal.value;
  } else {
    return storeCreditValue.value;
  }
});

const checkStoreCreditValue = computed(() => {
  return storeCreditValue.value > effectiveTotal.value;
});

function completeOrder() {
  if (orderStore.state.addManualOrder) {
    handleAddManualOrderCheckout();
  } else {
    handleCheckout();
  }
}

async function handleAddManualOrderCheckout() {
  const token = localStorage.getItem('token') || localStorage.getItem('managerToken');
  console.log('handleAddManualOrderCheckout');

  if (orderStore.hasZeroPriceItem) {
    toast.error('Item with 0.00 price can not be checked out.');
    return
  }

  if (loading.value) return;
  loading.value = true;

  const remaining = parseFloat(remainingAmount.value);

  if (!payLater.value && !orderStore.isThisPaidManualOrder) {
    if (selectedPaymentMethods.value.length === 0) {
      toast.error('Please select a payment method.');
      loading.value = false;
      return;
    }

    const totalAllocated = selectedPaymentMethods.value.reduce(
      (acc, method) => acc + parseFloat(method.amount || 0),
      0
    );

    if (remaining > 0.01) {
      toast.error('Payment allocation does not match the total amount.');
      loading.value = false; // Reset loading state
      return;
    }
  }

  const manualOrderPayload = {
    invoiceId: orderStore.state.InvoiceId || '',
    overallDiscount: orderStore.state.overallDiscount || 0,
    total: effectiveTotal.value,
    location: '',
    isManualOrder: true,
    approvalList: orderStore.state.approvalList.map(approval => ({
      itemId: approval.ItemId || null,
      managerApprovalId: approval.ManagerApprovalId || 'defaultApprovalId',
      managerId: approval.ManagerId || 'defaultManagerId',
      cashierId: authStore.currentUser?.id || '',
      id: approval.id || '',
      isOverallDiscount: approval.isOverallDiscount || false
    })),
    comments: orderStore.state.comments.map(comment => ({
      userId: comment.userId || authStore.managerUser.id,
      text: comment.text || ''
    })),
    itemList: orderStore.state.orderItems.map(item => ({
      itemId: item.ItemId,
      itemName: item.ItemName,
      qty: item.Qty,
      rate: item.isIncludeTaxChecked ? item.Price : item.OriginalPrice,
      discount: orderStore.state.overallDiscount > 0 ? orderStore.state.overallDiscount : parseFloat(item.Discount),
      taxesWaived: item.TaxesWaived,
      restockFee: 0,
      salesPersonId: item.SalesPersonId ? {
        id: item.SalesPersonId,
        fullName: '',
        role: ''
      } : null
    })),
    payment: payLater.value ? [] : selectedPaymentMethods.value.map(method => ({
      paymentMethodId: method.id,
      amount: (method.id === '7' && checkStoreCreditValue.value) ? effectiveTotal.value : method.amount,
      percentage: (method.amount / orderStore.state.total) * 100,
      change: method.name === 'Cash' ? changeAmount.value : '0.00',
      couponCode: (method.id === '7') ? storeCreditCode.value : '',
    })),
    taxes: orderStore.state.taxes.map(tax => ({
      taxId: tax.type,
      amount: tax.rate, 
    })),
    customer: {
      id: orderStore.state.customer.id || '',
      firstName: orderStore.state.customer.name ? orderStore.state.customer.name.split(' ')[0] : '',
      lastName: orderStore.state.customer.name ? orderStore.state.customer.name.split(' ').slice(1).join(' ') : '',
      phone: orderStore.state.customer.phone || '',
      email: orderStore.state.customer.email || '',
      note: orderStore.state.customer.note || '',
      addressLine1: orderStore.state.customer.addressLine1 || '',
      addressLine2: orderStore.state.customer.addressLine2 || '',
      city: orderStore.state.customer.city || '',
      state: orderStore.state.customer.state || '',
      zipCode: orderStore.state.customer.zipCode || '',
      country: orderStore.state.customer.country || ''
    },
    location: orderStore.state.pickUpInStore ? orderStore.state.pickupLocation : '',
    modeOfReturnId: 0,
  }
  try {
    if (!token) {
      toast.error('Authorization token not found. Please log in again.');
      loading.value = false;
      return;
    }

    const response = await axios.post(`/api/Order/NewManualOrder`, manualOrderPayload,
      {
        headers: {
          'store-id': storeId,
          'Authorization': `Bearer ${token}`
        }
      }
    );
    if (response.data.status === '200') {

       if (Math.abs(response.data.total - effectiveTotal.value) < .25) {
        toast.success('Order placed successfully.');
        invoiceId.value = response.data.invoiceId;

        const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        JsBarcode(svgNode, invoiceId.value.replace('SAL-ORD-', ''), {
          format: 'CODE128',
          lineColor: '#000',
          width: 2,
          height: 80,
          displayValue: false
        });
        const svgMarkup = svgNode.outerHTML
        barcodeSvgString.value = svgMarkup
        orderStore.state.isManualOrder = false;

        nextTick(() => {
          if (payLater.value) {
            handleClosePopup();
          } else {
            printInvoice();
          }
          orderStore.setIsThisPaidManualOrder(false);
        });

        isCheckoutSuccessful.value = true;
        wasNewSaleCompleted.value = true;

        // Save to local storage
        const toPass = generateInvoice.value;
        toPass.totalAmount = toPass?.totalAmount.value;
        toPass.isGiftReceipt = isGiftReceipt.value;
        toPass.payLater = payLater.value;

        if (!orderStore.isThisPaidManualOrder) {
          orderStore.saveReceiptToStorage(toPass);
        }

        //Deduct the qty locally
        orderStore.deductQuantities();
        
       } else {
         toast.error('Total in ERP and in the order don\'t match');
       }

    } else if (response.data.status === "500") {
      toast.error(`Error: ${response.data.message.join(', ')}`);
    } else {
      toast.error('Failed to place the order.');
    }
  } catch (error) {
    console.error('Error handling pay later:', error);
    toast.error('An error occurred while handling pay later.');
  } finally {
    loading.value = false;
  }
}

</script>

<style scoped>
@media print {

  body * {
    visibility: hidden !important;
  }

  .invoice-print {
    visibility: visible !important;
    display: block !important;
    width: 100% !important;
    max-width: 600px;
    margin: 0 auto !important;
    position: relative !important;
    background: transparent !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    page-break-inside: auto !important;
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }

  .invoice-print * {
    visibility: visible !important;
  }

  .invoice-print .class-hide-on-print {
    display: none !important;
  }

  .invoice-print table {
    width: 100%;
    border-collapse: collapse;
    page-break-inside: avoid;

  }

  .invoice-print thead {
    display: table-header-group !important;
  }

  .invoice-print tbody {
    display: table-row-group;
  }

  .invoice-print tfoot {
    display: table-row-group !important;
  }

  .invoice-print tr {
    page-break-inside: avoid !important;
  }

  .invoice-total {
    page-break-before: always !important;
    page-break-after: avoid !important;
  }

  .invoice-print>* {
    break-inside: avoid;
  }

  .watermark {
    display: block !important;
    position: absolute;
    top: 40%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 3rem;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.1);
    border: 4px solid rgba(0, 0, 0, 0.1);
    padding: 10px 20px;
    z-index: 10;
    pointer-events: none;
    white-space: nowrap;
  }
}

</style>



<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div v-show="authStore.isCheckoutPopupVisible && (authStore.isUserLoggedIn || authStore.isManagerLoggedIn)"
        class="fixed w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8 z-10">
        <Transition name="modal-inner" class="rounded-2xl">
          <div v-if="authStore.isCheckoutPopupVisible" @click.stop
            class="fixed top-10 z-50 flex items-center justify-center bg-black bg-opacity-30 w-10/12">
            <div class="flex bg-white rounded-2xl shadow-lg p-6 w-full">
              <i @click="handleClosePopup"
                class="class-hide-on-print pi pi-times-circle w-full text-right absolute top-3 right-3 cursor-pointer"
                :disabled="loading" :class="{ 'opacity-50 cursor-not-allowed': loading }" style="font-size: 24px;"></i>
              <div class="w-6/12 checkout-Left" :class="{ 'disabled-div': isCheckoutSuccessful }">
                <h1 class="font-semibold text-[24px] pb-4">Checkout</h1>

                <!-- Pay Later Toggle -->
                <PayLaterToggle v-model="payLater" v-show="orderStore.state.addManualOrder"/>

                <GiftReceiptToggle v-model="isGiftReceipt" v-show="!orderStore.state.addManualOrder && !orderStore.state.isManualOrder"/>

                <div v-if="!payLater" class="flex justify-between gap-3 mb-5 bg-gray-100 rounded-xl p-3"
                  :disabled="orderStore.isThisPaidManualOrder || payLater"
                  :class="{ 'opacity-50 cursor-not-allowed pointer-events-none': orderStore.isThisPaidManualOrder || payLater }"
                >
                  <button v-for="method in paymentMethods" :key="method.id" :disabled="remainingAmount === '0.00'"
                    class="w-24 h-28 border-2 px-3 py-2 rounded-xl bg-white hover:bg-gray-200 flex flex-col items-center justify-center text-center"
                    @click="addPaymentMethod(method)">
                    <img :src="method.icon" :alt="method.name" class="w-12 h-12 mb-1" />
                    <span class="text-[11px] leading-snug whitespace-normal break-words">
                      {{ method.name }}
                    </span>
                  </button>
                </div>

                <div v-if="!payLater" class="mb-5">
                  <h2 class="font-semibold text-lg mb-2">Selected Payments</h2>
                  <ul class="space-y-3 max-h-80 overflow-auto"
                    :class="{ 'opacity-50 cursor-not-allowed pointer-events-none': loading }">
                    <li v-for="method in selectedPaymentMethods" :key="method.id"
                      class="flex flex-col items-center justify-between bg-gray-100 p-3 rounded-lg shadow-sm relative group">

                      <!-- For Store Credit -->
                      <div v-if="method.name === 'Store Credit'" class="bg-gray-100 w-full rounded-xl p-3 flex flex-col gap-2">
                       <div class="flex items-center gap-2">
                         <input type="text" v-model="storeCreditCode" placeholder="Enter Coupon Code"
                           @keydown.enter="getStoreCreditValue"
                           class="border-none outline-none rounded-md py-2 px-3 w-full"
                           :disabled="storeCreditValue > 0 || isLoadingCreditValue"
                         >
                         <button 
                           @click="getStoreCreditValue"
                           :disabled="storeCreditValue > 0 || isLoadingCreditValue || storeCreditCode.length !== 10"
                           class="w-48 bg-purple-500 text-white px-6 py-2 rounded-xl hover:bg-purple-600 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-purple-500">
                           Load value
                         </button>
                       </div>
                     
                       <p v-if="storeCreditValue">
                         <span>Store Credit Value:</span> <span class="text-purple-500">${{ storeCreditValue }}</span>
                       </p>
                      </div>

                      <div class="flex w-full justify-between px-5 py-3">
                        <img :src="paymentMethods.find(pm => pm.id === method.id).icon" alt="" class="w-10 h-10">
                        <div class="relative flex items-center gap-4">
                          <input 
                            v-if="method.name === 'Store Credit'"
                            type="text" 
                            v-model="isStoreCreditValueGreaterThanTotal"
                            @input="updatePaymentAmount(method, method.amount)"
                            @keydown="preventNegativeInput"
                            @blur="amountShouldNotExceedstoreCreditValue(method, method.amount)"
                            :class="[
                              'border-2 rounded-md py-1 px-3 w-32 text-right focus:ring focus:outline-none pr-10',
                              method.type === 'Store Credit' 
                                ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-500' 
                                : 'border-gray-300 focus:ring-purple-200 focus:border-purple-500'
                            ]"
                          >
                          <input 
                            v-if="method.id !== '7'"
                            type="text" 
                            v-model="method.amount" 
                            @input="updatePaymentAmount(method, method.amount)"
                            @keydown="preventNegativeInput"
                            :class="[
                              'border-2 rounded-md py-1 px-3 w-32 text-right focus:ring focus:outline-none pr-10',
                              method.type === 'Store Credit' 
                                ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-500' 
                                : 'border-gray-300 focus:ring-purple-200 focus:border-purple-500'
                            ]"
                          >

                          <!-- Clear Button inside Input -->
                          <button v-if="method.amount > 0" @click="clearPaymentAmount(method)"
                            class="absolute right-2 top-2 text-gray-500 hover:text-gray-700 text-lg"
                            style="font-size: 14px;">
                            <i class="pi pi-times"></i>
                          </button>
                        </div>
                      </div>

                      <!-- Remove Payment Method Button - Visible on Hover -->
                      <button @click="removePaymentMethod(method.id)"
                        class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:gray-red-700">
                        <i class="pi pi-times"></i>
                      </button>

                      <!-- Predictive Cash Amounts if Cash is selected -->
                      <div v-if="method.name === 'Cash'" class="w-full flex gap-2 justify-end px-5">
                        <button v-for="amount in predictiveCashAmounts" :key="amount" @click="setCashAmount(amount)"
                          class="bg-gray-200 p-2 rounded hover:bg-gray-300">
                          ${{ amount }}
                        </button>
                      </div>

                      <!-- Set Exact Total Button for Credit Card Payments -->
                      <div
                        v-if="['Visa', 'Master Card', 'Amex', 'Debit Card', 'Interac Etransfer'].includes(method.name)"
                        class="w-full flex gap-2 justify-end px-5">
                        <button @click="setExactTotal(method)" class="bg-gray-200 p-2 rounded hover:bg-gray-300">
                          ${{ effectiveTotal }}
                        </button>
                      </div>

                      <p v-if="method.name === 'Cash' && parseFloat(changeAmount) > 0"
                        class="text-purple-500 ml-auto mr-5 mt-2">
                        Change: ${{ changeAmount }}
                      </p>
                    </li>
                  </ul>

                  <div v-show="!orderStore.isThisPaidManualOrder" class="flex items-center justify-between mt-2">
                    <p :class="{ 'text-green-500': remainingAmount == 0, 'text-red-500': remainingAmount > 0 }">
                      Remaining balance: ${{ remainingAmount }}
                      <span v-if="remainingAmount == 0" class="ml-2 text-green-500"><i class="pi pi-check"></i></span>
                    </p>
                  </div>
                </div>

                <p class="text-xl font-semibold mb-5">Total:${{ effectiveTotal }}</p>

                <div class="flex justify-end gap-3">
                  <!--<button @click="showInvoice"
                    class="bg-purple-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-600 transition">
                    Invoice
                  </button>
                  -->

                  <button @click="completeOrder" :disabled="isCheckoutSuccessful || loading || (isRemainingAmountPositive && !orderStore.isThisPaidManualOrder && !payLater)"
                    class="bg-purple-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-600 transition"
                    :class="{ 'opacity-50 cursor-not-allowed': isCheckoutSuccessful || loading || (isRemainingAmountPositive && !orderStore.isThisPaidManualOrder && !payLater) }">
                    {{ loading ? 'Processing...' : 'Confirm and Checkout' }}
                  </button>
                </div>
              </div>
              <!-- Invoice Section -->

              <div v-if="isInvoiceVisible && !payLater"
                class="invoice-div invoice-print w-6/12 ml-16 mr-5 p-6 bg-gray-100 rounded-lg shadow-lg relative group max-h-[700px] overflow-y-scroll">
                <div v-if="orderStore.state.addManualOrder && !payLater" class="watermark">
                  PAID
                </div>
                <div v-if="orderStore.state.receiptForReprint && orderStore.state.receiptForReprint.payLater" class="watermark">
                  NOT PAID
                </div>
                <div v-if="isGiftReceipt" class="watermark">
                  GIFT RECEIPT
                </div>
                <div v-if="orderStore.state.receiptForReprint && !generateInvoice?.isThisReturnReciept" class="class-hide-on-print mb-4">
                   <GiftReceiptToggle v-model="isGiftReceipt" />
                </div>
                <button @click="printInvoice" v-if="orderStore.state.receiptForReprint || orderStore.state.returnPrint"
                  class="class-hide-on-print w-full bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition flex items-center justify-center gap-2">
                  <!-- Print icon -->
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M6 9V2h12v7M6 18h12v4H6v-4zM6 14h.01M18 14h.01M4 9h16v5H4V9z" />
                  </svg>
                  Print
                </button>
                <div v-if="orderStore.state.receiptForReprint" class="p-2 mb-4 rounded">
                  <i class="pi pi-print mr-2"></i> Reprint of saved receipt
                </div>
                <div v-else-if="orderStore.state.returnPrint" class="bg-yellow-100 text-yellow-800 p-2 mb-4 rounded">
                  <i class="pi pi-print mr-2"></i> Return receipt
                </div>
                <h2 class="text-xl font-bold mb-3 break-words whitespace-normal" v-if="currentBranch['Header1']"
                  v-html="convertNewlines(currentBranch['Header1'])"></h2>
                <h3 class="text-lg font-semibold mb-3 break-words whitespace-normal" v-if="currentBranch['Header2']"
                  v-html="convertNewlines(currentBranch['Header2'])"></h3>
                
                <p v-if="orderStore.state.returnPrint"><strong>Invoice ID:</strong> SAL-ORD-{{ generateInvoice?.invoiceId || 'N/A' }}</p>
                <p v-else><strong>Invoice ID:</strong> {{ generateInvoice?.invoiceId || 'N/A' }}</p>
                <p v-if="orderStore.state.returnPrint && orderStore.state.returnPrint.customer !== 'DefaultPOSEdmonds'"><strong>Customer:</strong> {{ generateInvoice.customerName }}</p>
                <p v-if="orderStore.state.returnPrint"><strong>Date:</strong> {{ orderStore.formatDate(Date.now()) }}</p>
                <div class="mb-5">
                  <div v-if="!orderStore.state.receiptForReprint && !orderStore.state.returnPrint">
                    
                    <p v-if="orderStore.state.customer && orderStore.state.customer.name"><strong>Customer:</strong> {{ generateInvoice.customerName }}</p>
                    <p><strong>Date:</strong> {{ orderStore.formatDate(Date.now()) }}</p>
                    <!-- <p v-if="orderStore.isSalesPersonSelected || orderStore.isSalesPersonForManualOrder"><strong>Sold by:</strong>
                      {{ generateInvoice.Salesperson }}
                    </p> -->
                  </div>
                  <div class="" v-if="orderStore.state.receiptForReprint">
                    <!-- <p v-if="generateInvoice.customerName"><strong>Customer:</strong> {{ generateInvoice.customerName }}</p> -->
                    <p><strong>Date:</strong> {{ orderStore.formatDate(Date.now()) }}</p>
                    <p><strong>Re-printed by: </strong>{{ generateInvoice.issueReprint }}</p>
                    <!--<p v-if="generateInvoice.Salesperson"><strong>Sold by:</strong>
                      {{ generateInvoice.Salesperson }}
                    </p>-->
                  </div>
                </div>

                <table class="w-full mb-5 border-collapse">
                  <thead>
                    <tr class="border-b-2 border-gray-300">
                      <th class="text-left py-2" :class="isGiftReceipt ? 'w-10/12' : 'w-5/12'">Item</th>
                      <th class="text-center py-2 w-2/12">Qty</th>
                      <th v-if="!isGiftReceipt" class="text-right py-2 w-1/6">Price</th>
                      <th v-if="!isGiftReceipt" class="text-right py-2 w-3/12">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="generateInvoice?.items?.length > 0">
                      <template v-for="(item, index) in generateInvoice.items" :key="index">
                        <!-- Item Row -->
                        <tr>
                          <td class="py-2 overflow-hidden text-ellipsis" style="max-width: 200px;"
                            >
                            <span >{{item.sku}} <br> {{ item.name }}</span><br>
                            <span v-if="orderStore.state.returnPrint || generateInvoice.isThisReturnReciept" :class="{ 'line-through':  orderStore.state.returnPrint || generateInvoice.isThisReturnReciept}">
                              {{item.sku}} <br> {{ item.name }}
                            </span>
                            
                            <span v-if="orderStore.state.returnPrint || generateInvoice.isThisReturnReciept" class="no-underline"> Returned</span>
                          </td>
                          <td class="py-2 text-center">
                              <span v-if="generateInvoice.isThisReturnReciept">{{ item.originalQty }}</span>
                              <span v-if="!orderStore.state.returnPrint && !generateInvoice.isThisReturnReciept">{{ item.quantity }}</span>
                              <span v-if="orderStore.state.returnPrint">{{ item.orgQty }}</span>
                              <br>
                              
                              <span v-if="orderStore.state.returnPrint || generateInvoice.isThisReturnReciept">- {{ item.quantity }}</span>

                          </td>
                          <td v-if="!isGiftReceipt" class="py-2 text-right">
                            <div class="">
                              <span v-if="item.discount && item.discount > 0">
                              <span class="line-through mr-1">${{ item.originalPrice.toFixed(2) }}</span>
                              </span>
                              <span v-else>
                                ${{ Number(item.price).toFixed(2) }}
                              </span>
                            </div>
                            <div v-if="orderStore.state.returnPrint || generateInvoice.isThisReturnReciept" class="">
                              <span v-if="item.discount && item.discount > 0">
                                <span class="line-through mr-1">- ${{ item.originalPrice.toFixed(2) }}</span>
                              </span>
                              <span v-else>
                                - ${{ Number(item.price).toFixed(2) }}
                              </span>
                            </div>
                          </td>
                          <td v-if="!isGiftReceipt" class="py-2 text-right">
                            <span>
                              ${{ Number(item.total).toFixed(2) }}
                            </span>
                            <br>
                            <span v-if="orderStore.state.returnPrint || generateInvoice.isThisReturnReciept"> 
                              - ${{ Number(item.total).toFixed(2) }}
                            </span>
                          </td>
                        </tr>

                        <!-- Discount Row -->
                        <tr v-if="!isGiftReceipt && item.discount && item.discount > 0" class="text-sm text-gray-500">
                          <td class="py-1 pl-4" colspan="4">
                            {{ Number(item.discount).toFixed(2) }}% off — You saved ${{ (Number(item.originalPrice) - Number(item.price)).toFixed(2) }} per item
                          </td>
                        </tr>

                        <!-- Bottom Border Row AFTER item group -->
                        <tr class="border-b-2 border-gray-300">
                          <td colspan="4"></td>
                        </tr>
                      </template>
                    </template>




                    <!-- Fallback message if no items exist -->
                    <tr v-else>
                      <td :colspan="isGiftReceipt ? 2 : 4" class="text-center py-4">No items found in the invoice.</td>
                    </tr>
                  </tbody>
                  <thead v-if="!isGiftReceipt">
                    <tr class="border-b-2 border-gray-300">
                      <th class="text-left py-2 w-6/12">Payment Method</th>
                      <th class="text-right py-2 w-1/6"></th>
                      <th class="text-right py-2 w-1/6"></th>
                      <th class="text-right py-2 w-6/12">Amount</th>
                    </tr>
                  </thead>
                  <tbody v-if="!isGiftReceipt">
                    <tr v-for="(method, index) in generateInvoice?.selectedPaymentMethods" :key="index"
                      class="border-b-2 border-gray-300">
                      <td class="py-2 text-left">{{ method.name }}:</td>
                      <td class="py-2 text-left"></td>
                      <td class="py-2 text-left"></td>
                      <td v-if="checkStoreCreditValue" class="py-2 text-right">
                        <span>${{ effectiveTotal || '0.00' }}</span>
                      </td>
                      <td v-else class="py-2 text-right">
                        <span>${{ method.amount || '0.00' }}</span>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <template v-if="!isGiftReceipt">
                    <tr>
                      <td colspan="3" class="text-right font-semibold">Subtotal:</td>
                      <td class="text-right">${{ generateInvoice?.subtotal || '0.00' }}</td>
                    </tr>
                    <tr v-if="generateInvoice?.discountAmount > 0">
                      <td colspan="3" class="text-right font-semibold">Discount:</td>
                      <td class="text-right">- ${{ generateInvoice?.discountAmount || '0.00' }}</td>
                    </tr>
                    <!-- <tr v-if="generateInvoice?.taxes?.length > 0">
                      <td colspan="3" class="text-right font-semibold">{{ generateInvoice.taxes[0]?.type }}:</td>
                      <td class="text-right">${{ (generateInvoice.taxes[0]?.amount || 0)}}</td>
                    </tr>
                    <tr v-if="generateInvoice?.taxes?.length > 1">
                      <td colspan="3" class="text-right font-semibold">{{ generateInvoice.taxes[1]?.type }}:</td>
                      <td class="text-right">${{ (generateInvoice.taxes[1]?.amount || 0)}}</td>
                    </tr> -->

                    <!-- Fallback for old format -->
                    <tr v-if="generateInvoice?.gst && generateInvoice.taxes[0]?.type">
                      <td colspan="3" class="text-right font-semibold">{{ generateInvoice.taxes[0].type }}:</td>
                      <td class="text-right">${{ Number(generateInvoice.gst).toFixed(2) }}</td>
                    </tr>
                    <tr v-if="generateInvoice?.pst && generateInvoice.taxes[1]?.type">
                      <td colspan="3" class="text-right font-semibold">{{ generateInvoice.taxes[1].type }}:</td>
                      <td class="text-right">${{ Number(generateInvoice.pst).toFixed(2) }}</td>
                    </tr>
                    <tr v-if="(orderStore.state.returnPrint && orderStore.state.returnPrint.totalRestockingFee > 0) || (generateInvoice.isThisReturnReciept && generateInvoice.restockingFeeTotal > 0)">
                      <td colspan="3" class="text-right font-semibold">Restocking Fee:</td>
                      <td class="text-right">${{ generateInvoice?.restockingFeeTotal.toFixed(2) || '0.00' }}</td>
                    </tr>
                    <tr class="font-bold">
                      <td colspan="3" class="text-right font-bold">Total:</td>
                      <td class="text-right font-bold">${{ generateInvoice?.totalAmount || '0.00' }}</td>
                    </tr>
                    <tr v-if="generateInvoice?.changeAmount && generateInvoice.changeAmount > 0">
                      <td colspan="3" class="text-right text-base">Change:</td>
                      <td class="text-right text-base">${{ generateInvoice?.changeAmount || '0.00' }}</td>
                    </tr>
                    </template>
                  </tfoot>
                </table>

                <div class="text-center">
                  <!-- <div class="barcode"> -->
                  <div v-if="orderStore.state.returnPrint" class="flex justify-center">
                    <div v-html="barcodeSvgStringForOrderId"></div>
                  </div>
                  <p v-if="orderStore.state.returnPrint && (orderStore.state.returnPrint.refundMethod || authStore.selectedRefundMethod) === 'StoreCredit'">You now have a store credit of ${{ orderStore.state?.returnPrint?.totalAmount }} please bring this reciept to be able to use this code.</p>
                  
                  <p v-if="orderStore.state.returnPrint && (orderStore.state.returnPrint.refundMethod || authStore.selectedRefundMethod) === 'Cash'">You have been refunded {{ orderStore.state?.returnPrint?.totalAmount }} in <strong>Cash.</strong></p>
                  
                  <p v-if="orderStore.state.returnPrint && !['StoreCredit', 'Cash'].includes(orderStore.state.returnPrint.refundMethod || authStore.selectedRefundMethod)">You have been refunded {{ orderStore.state?.returnPrint?.totalAmount }} to the original payment method(s).</p>
                  
                  <div class="flex justify-center">
                    <div v-html="barcodeSvgString"></div>
                  </div>
                  <p v-if="generateInvoice.isThisReturnReciept && generateInvoice.isThisCashReturn">You have been refunded {{ generateInvoice.totalAmount }} in <strong>Cash.</strong></p>
                  <p v-if="generateInvoice.totalAmount > 0 && generateInvoice.isThisReturnReciept && (generateInvoice.refundMethod === 'StoreCredit')">You now have a store credit of ${{ generateInvoice.totalAmount }} please bring this reciept to be able to use this code.</p>
                  <p v-if="generateInvoice.isThisReturnReciept && !generateInvoice.isThisCashReturn && generateInvoice.refundMethod !== 'StoreCredit'">You have been refunded {{ generateInvoice.totalAmount }} to the original payment method(s).</p>

                  <div v-if="orderStore.state.returnPrint" class="flex justify-center">
                    <p v-if="orderStore.state.returnPrint && authStore.selectedRefundMethod === 'StoreCredit'">{{ couponCode }}</p>
                  </div>

                  <div v-if="generateInvoice.isThisReturnReciept && !generateInvoice.isThisCashReturn" class="flex justify-center">
                    <div v-html="barcodeSvgForCouponCodeReprint"></div>
                  </div>

                  <div v-if="generateInvoice.isThisReturnReciept" class="flex justify-center">
                    <p>{{ generateInvoice.couponCodeForReprint }}</p>
                  </div>

                  <div v-if="remainingStoreCredit > 0 || generateInvoice.remainingStoreCredit > 0" class="flex flex-col justify-center">
                    <p>You still have a store credit of ${{ generateInvoice.remainingStoreCredit }}.</p>
                    <!-- <div class="flex justify-center">
                      <div v-html="barcodeSvgStringForRemainingStoreCredit"></div>
                    </div>
                    <p>{{ couponCode }}</p> -->
                  </div>
                </div>
                
                
                <div v-if="currentBranch['Footer']" class="whitespace-normal break-words text-sm"
                  v-html="convertNewlines(currentBranch['Footer'])"></div>
                <!-- Button to generate PDF -->
                <div class="flex justify-end class-hide-on-print ">
                  <!-- <button @click="downloadPdf"
                    class="class-hide-on-print  bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition">
                    Download PDF
                  </button> -->

                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>

    <Transition name="modal-outer">
      <div
        v-if="showFrontendIssueModal"
        class="fixed inset-0 z-[120] flex items-center justify-center bg-black bg-opacity-50"
      >
        <Transition name="modal-inner" class="rounded-2xl">
          <div class="bg-white rounded-2xl shadow-lg p-6 w-full max-w-xl">
            <h2 class="text-xl font-semibold mb-4">Order Processing Error</h2>
            <p class="text-gray-700 mb-6">
              If this is the first time you are seeing this error, click the button below to rescan the order items.
            </p>
            <div class="flex justify-end gap-3">
              <button
                type="button"
                class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                @click="showFrontendIssueModal = false"
              >
                Close
              </button>
              <button
                type="button"
                class="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700"
                @click="handleRescanOrderItems"
              >
                Rescan Order Items
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>


<style scoped>
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.disabled-div {
  opacity: 0.5;
  pointer-events: none;
  user-select: none;
}

.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}

.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}

.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-inner-leave-to {
  transform: scale(0.8);
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

.no-underline {
  text-decoration: none !important;
}

.watermark {
  display: block;
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
  font-size: 3rem;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.1);
  border: 4px solid rgba(0, 0, 0, 0.1);
  padding: 10px 20px;
  z-index: 10;
  pointer-events: none;
  white-space: nowrap;
}
</style>